import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";

import { Skill } from "./types";
import { preventSpecialCharKeyDownInTextfieldAllowPlus } from "util/preventSpecialCharInput";

interface Props {
  skill: Skill;
  onUpdate: (id: number, skill: string) => void;
  onDelete: (id: number) => void;
}

export default function SkillCard({
  skill,
  onUpdate,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(skill.skill);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = () => {
    // 1. Check if completely empty
    if (value.length === 0) {
      setError("Skill name cannot be empty");
      return;
    }

    // 2. Check if it contains ONLY spaces
    if (value.trim().length === 0) {
      setError("Skill name cannot contain only spaces");
      return;
    }

    // Pass validation, trigger update
    onUpdate(skill.id, value);
    setEditing(false);
    setError("");
  };

  const handleCancel = () => {
    setEditing(false);
    setValue(skill.skill);
    setError("");
  };

  return (
    <div className="w-full h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm hover:shadow-md transition">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex flex-col w-full">
              <input
                onKeyDown={(e) => {
                  preventSpecialCharKeyDownInTextfieldAllowPlus(e);
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
                ref={inputRef}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (error) setError(""); // Clear error when typing
                }}
                className={`h-10 w-full border rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition ${error
                    ? "border-red-400 focus:ring-red-400"
                    : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                  }`}
              />
              {error && (
                <p className="mt-1 text-xs font-medium text-red-500">
                  {error}
                </p>
              )}
            </div>
          ) : (
            <div className="flex h-10 items-center px-3">
              <span className="block truncate font-semibold text-gray-700 dark:text-white">
                {skill.skill}
              </span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 h-10 py-1">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                className="text-green-600 p-1.5 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition"
                title="Save (Enter)"
              >
                <FaCheck />
              </button>

              <button
                onClick={handleCancel}
                className="text-red-500 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                title="Cancel (Escape)"
              >
                <FaTimes />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="btn-primary px-2 py-2 rounded-lg"
                title="Edit"
              >
                <FaEdit />
              </button>

              <button
                onClick={() => onDelete(skill.id)}
                className="btn-red px-2 py-2 rounded-lg"
                title="Delete"
              >
                <FaTrash />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}